import React, { useEffect, useState, useContext } from 'react';
import { UserContext } from '../UserContext';
import axios from '../axiosInstance';
import { FaEdit, FaPlus, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { CSVLink } from 'react-csv';
import * as XLSX from 'xlsx';
import { exportBrandedPdf } from '../utils/brandedPdf';
import '../Pages.css';
import Pagination from '../components/Pagination';
import { SHOW_MTC_MENUS } from '../featureFlags';
import { formatDate, formatDateTime } from '../utils/dateFormat';

function ManageOrganisations() {
  const { user } = useContext(UserContext);
  const [organisations, setOrganisations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    package_id: null,
    menus: []
  });
  const [message, setMessage] = useState('');
  const [showMessagePopup, setShowMessagePopup] = useState(false);
  const [packages, setPackages] = useState([]);
  const [menuCatalog, setMenuCatalog] = useState([]);
  const canManageMenus = !!(user && user.is_system_admin);
  const allMenuKeys = menuCatalog.map(m => m.key);

  const [itemsPerPage, setItemsPerPage] = useState(5);

  useEffect(() => {
    fetchOrganisations();
    fetchPackages();
    fetchMenuCatalog();
  }, []);

  const fetchOrganisations = async () => {
    try {
      const res = await axios.get('/organisations');
      setOrganisations(res.data);
    } catch (err) {
      console.error('Error fetching organisations:', err);
    }
  };

  const fetchPackages = async () => {
    try {
      const res = await axios.get('/packages');
      setPackages(res.data);
    } catch (err) {
      console.error('Error fetching packages:', err);
    }
  };

  const fetchMenuCatalog = async () => {
    try {
      const res = await axios.get('/organisations/menus');
      setMenuCatalog(res.data);
    } catch (err) {
      console.error('Error fetching menus:', err);
    }
  };

  const menuLabel = (key) => menuCatalog.find(m => m.key === key)?.label || key;

  const describeMenus = (menus = []) => {
    if (menuCatalog.length && allMenuKeys.every(k => menus.includes(k))) return 'All menus';
    if (!menus.length) return 'None';
    return menuCatalog.filter(m => menus.includes(m.key)).map(m => m.label).join(', ');
  };

  const toggleMenu = (key) => {
    setFormData(fd => ({
      ...fd,
      menus: fd.menus.includes(key) ? fd.menus.filter(k => k !== key) : [...fd.menus, key]
    }));
  };

  const handleSort = (col) => {
    const order = sortColumn === col && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortColumn(col);
    setSortOrder(order);
  };

  const filtered = searchTerm
    ? organisations.filter(o =>
        Object.values(o).join(' ').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : organisations;

  const sorted = filtered.sort((a, b) => {
    const valA = (a[sortColumn] || '').toString().toLowerCase();
    const valB = (b[sortColumn] || '').toString().toLowerCase();
    return valA < valB ? (sortOrder === 'asc' ? -1 : 1) : valA > valB ? (sortOrder === 'asc' ? 1 : -1) : 0;
  });

  const totalPages = Math.ceil(sorted.length / itemsPerPage);
  const currentItems = sorted.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);


  const toggleStatus = async (org) => {
    try {
      await axios.patch(`/organisations/${org.id}`, { is_active: !org.is_active });
      setMessage('✅ Organisation status updated');
      setShowMessagePopup(true);
      fetchOrganisations();
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.error || err.message));
      setShowMessagePopup(true);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setShowMessagePopup(false);

    const url = editMode
      ? `/organisations/${editId}`
      : '/organisations';
    const method = editMode ? 'put' : 'post';

    const { menus, ...orgFields } = formData;

    try {
      let packageFrom = null;
      if (editMode) {
        const res = await axios.put(url, orgFields);
        packageFrom = res.data?.organisation?.package_effective_from || null;
        if (canManageMenus) await axios.put(`/organisations/${editId}/menus`, { menus });
      } else {
        await axios[method](url, canManageMenus ? formData : orgFields);
      }
      setMessage(editMode
        ? `✅ Organisation updated${packageFrom ? `. The new package is invoiced from ${formatDate(packageFrom)}; earlier months stay on the old package.` : ''}`
        : '✅ Organisation added');
      setShowMessagePopup(true);
      resetForm();
      fetchOrganisations();
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.error || err.message));
      setShowMessagePopup(true);
    }
  };

  const resetForm = () => {
    setFormData({ 
      name: '', 
      description: '', 
      contact_email: '', 
      contact_phone: '', 
      address: '',
      package_id: null,
      menus: []
    });
    setEditMode(false);
    setEditId(null);
    setShowForm(false);
  };

  const handleEdit = (org) => {
    setFormData({
      name: org.name,
      description: org.description,
      contact_email: org.contact_email || '',
      contact_phone: org.contact_phone || '',
      address: org.address || '',
      package_id: org.package_id || null,
      // Only menus in the current catalogue (retired keys would fail validation)
      // (the server already drops those; this only runs once the catalogue is loaded)
      menus: Array.isArray(org.menus)
        ? (allMenuKeys.length ? org.menus.filter(k => allMenuKeys.includes(k)) : org.menus)
        : []
    });
    setEditMode(true);
    setEditId(org.id);
    setShowForm(true);
  };

  const exportData = organisations.map(o => ({
    Name: o.name,
    Description: o.description,
    Email: o.contact_email,
    Phone: o.contact_phone,
    Address: o.address,
    Package: o.package_name || 'None',
    Menus: describeMenus(o.menus),
    Status: o.is_active ? 'Active' : 'Inactive',
    'Created At': formatDateTime(o.created_at)
  }));

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Organisations');
    XLSX.writeFile(wb, 'organisations.xlsx');
  };

  const exportPDF = () => exportBrandedPdf({
    title: 'ORGANISATIONS — REPORT',
    user,
    searchValue: 'All organisations',
    fileName: 'Organisations_Report',
    columns: [
      { header: 'Name', width: 16, bold: true }, { header: 'Description', width: 16 },
      { header: 'Email', width: 15 }, { header: 'Phone', width: 10 }, { header: 'Address', width: 14 },
      { header: 'Package', width: 9 }, { header: 'Menus', width: 20 }, { header: 'Status', width: 7 },
      { header: 'Created At', width: 11 }
    ],
    rows: exportData.map(o => Object.values(o))
  });

  return (
    <div className="page-container">
      <div className="table-controls">
        <input
          type="text"
          placeholder="Search organisations..."
          value={searchTerm}
          onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
        />
        <div className="button-group">
          {/* Only system admins can add new organizations */}
          {user && user.is_system_admin && (
            <button className="export-button" onClick={() => {
              setEditMode(false);
              setEditId(null);
              setShowForm(true);
              setFormData({ name: '', description: '', contact_email: '', contact_phone: '', address: '', package_id: null, menus: allMenuKeys });
            }}>
              <FaPlus style={{ marginRight: 5 }} /> Add Organisation
            </button>
          )}
          <CSVLink filename="organisations.csv" data={exportData} className="export-button">Export CSV</CSVLink>
          <button className="export-button" onClick={exportExcel}>Export Excel</button>
          <button className="export-button" onClick={exportPDF}>Export PDF</button>
        </div>
      </div>
      

      {showForm && (
        <form className="table-container" onSubmit={handleFormSubmit}>
          <h3>{editMode ? 'Edit Organisation' : 'Add New Organisation'}</h3>
          
          <div className="form-group">
            <input 
              placeholder="Organisation Name" 
              required 
              value={formData.name} 
              onChange={e => setFormData({ ...formData, name: e.target.value })} 
            />
          </div>

          <div className="form-group">
            <textarea 
              placeholder="Description" 
              value={formData.description} 
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="form-group name-group">
            <input 
              placeholder="Contact Email" 
              type="email" 
              value={formData.contact_email} 
              onChange={e => setFormData({ ...formData, contact_email: e.target.value })} 
            />
            <input 
              placeholder="Contact Phone" 
              value={formData.contact_phone} 
              onChange={e => setFormData({ ...formData, contact_phone: e.target.value })} 
            />
          </div>

          <div className="form-group">
            <textarea 
              placeholder="Address" 
              value={formData.address} 
              onChange={e => setFormData({ ...formData, address: e.target.value })}
              rows="2"
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="form-group">
            <label>Select Package:</label>
            <div className="package-cards-container">
              {packages.map(pkg => (
                <div 
                  key={pkg.id} 
                  className={`package-card ${formData.package_id === pkg.id ? 'selected' : ''}`}
                  onClick={() => setFormData({ ...formData, package_id: pkg.id })}
                >
                  <div className="package-card-header">
                    <input
                      type="radio"
                      name="package"
                      checked={formData.package_id === pkg.id}
                      onChange={() => setFormData({ ...formData, package_id: pkg.id })}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="package-name">{pkg.name}</span>
                  </div>
                  <div className="package-details">
                    <div className="package-price">
                      {pkg.price_monthly ? `$${pkg.price_monthly}/month` : 'Free'}
                    </div>
                    <ul className="package-features">
                      <li>Users: {pkg.user_limit === null ? 'Unlimited' : pkg.user_limit}</li>
                      <li>Onboarding Screenings: {pkg.onboarding_screening_limit === null ? 'Unlimited' : pkg.onboarding_screening_limit}</li>
                      {pkg.batch_screening_limit == null && pkg.batch_screening_limit_monthly == null ? (
                        <li>Batch Screenings: Unlimited</li>
                      ) : (
                        <>
                          <li>Batch Screenings (once-off): {pkg.batch_screening_limit ?? 'None'}</li>
                          <li>Batch Screenings (monthly): {pkg.batch_screening_limit_monthly ?? 'None'}</li>
                        </>
                      )}
                      {pkg.allow_export && <li>✓ Export Data</li>}
                      {pkg.allow_audit_trail && <li>✓ Audit Trail</li>}
                      {pkg.allow_batch_screening && <li>✓ Batch Screening</li>}
                      {pkg.allow_system_integration && <li>✓ System Integration</li>}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {canManageMenus && (
            <div className="form-group">
              <label>
                Menus ({formData.menus.length} of {menuCatalog.length} assigned):
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', margin: '0.25rem 0 0.5rem' }}>
                <button type="button" className="export-button" onClick={() => setFormData(fd => ({ ...fd, menus: allMenuKeys }))}>
                  Select all
                </button>
                <button type="button" className="btn-cancel" onClick={() => setFormData(fd => ({ ...fd, menus: [] }))}>
                  Clear
                </button>
              </div>
              <div className="package-cards-container">
                {menuCatalog.map(m => {
                  const assigned = formData.menus.includes(m.key);
                  return (
                    <div
                      key={m.key}
                      className={`package-card ${assigned ? 'selected' : ''}`}
                      onClick={() => toggleMenu(m.key)}
                    >
                      <div className="package-card-header">
                        <input
                          type="checkbox"
                          checked={assigned}
                          onChange={() => toggleMenu(m.key)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="package-name">{m.label}</span>
                      </div>
                      <div className="package-details">
                        {assigned ? 'Assigned' : 'Not assigned'}
                        {m.mtc && !SHOW_MTC_MENUS && ' · MTC (hidden until enabled)'}
                        {m.mtc && SHOW_MTC_MENUS && ' · MTC'}
                      </div>
                    </div>
                  );
                })}
              </div>
              <small>Data Capturer, Administrator and Audit Trail are always available.</small>
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="export-button">
              {editMode ? 'Update' : 'Submit'}
            </button>
            <button 
              type="button" 
              className="btn-cancel" 
              onClick={resetForm}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {showMessagePopup && (
        <div className="message-popup">
          <div className="message-popup-content">
            <p>{message}</p>
            <button className="close-popup-button" onClick={() => setShowMessagePopup(false)}>Close</button>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '1rem', fontWeight: 'bold', fontSize: '1rem' }}>
        Showing {currentItems.length} of {filtered.length} organisations
      </div>

      <div className="table-container">
        <table className="pips-table organisations-table">
          <thead>
            <tr>
              <th>#</th>
              <th onClick={() => handleSort('name')}>Name</th>
              <th>Description</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Address</th>
              <th>Package</th>
              <th>Menus</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map((o, idx) => (
              <tr key={o.id}>
                <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                <td>{o.name}</td>
                <td>{o.description}</td>
                <td>{o.contact_email}</td>
                <td>{o.contact_phone}</td>
                <td>{o.address}</td>
                <td>{o.package_name || 'None'}</td>
                <td title={(o.menus || []).map(menuLabel).join(', ')}>{describeMenus(o.menus)}</td>
                <td>{o.is_active ? 'Active' : 'Inactive'}</td>
                <td>
                  <button title="Edit" className="action-button" onClick={() => handleEdit(o)}><FaEdit /></button>
                  {/* Only system admins can toggle organization status */}
                  {user && user.is_system_admin && (
                    <button title={o.is_active ? 'Deactivate' : 'Activate'} className="action-button" onClick={() => toggleStatus(o)}>
                      {o.is_active ? <FaToggleOn className="toggle-icon--on" /> : <FaToggleOff className="toggle-icon--off" />}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={sorted.length}
        itemsPerPage={itemsPerPage}
        itemLabel="organisation"
        onPageChange={setCurrentPage}
        onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
      />
    </div>
  );
}

export default ManageOrganisations;
